import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import {
  BulkUploadProgressService,
  BulkUploadState,
} from './bulk-upload-progress.service';

@Component({
  selector: 'app-bulk-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './bulk-create-rate-modal.component.html',
  styleUrl: './bulk-create-rate-modal.component.scss',
})
export class BulkCreateRateModalComponent implements OnInit, OnDestroy {
  rateForm!: FormGroup;
  isSubmitting = false;

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  categoryTypeArray: Array<any> = [];
  selectedCategoryID!: number;
  selectedCategory: string = '';

  currentStep: number = 1;

  isSaveButtonEnabled: boolean = false;

  isDragOver = false;
  uploadedFile: File | null = null;

  isPreviewed = false;
  previewData: any = null;
  activeTab: 'errors' | 'warnings' = 'errors';

  showProcessingLog = false;
  uploadCompleted = false;
  fileError: string | null = null;

  private uploadState: BulkUploadState;
  private stateSub?: Subscription;
  private resultApplied = false;
  private failApplied = false;

  // Steps shown in the wizard progress indicator.
  steps = [
    { label: 'Select Category' },
    { label: 'Select File' },
    { label: 'Upload & Validate' },
  ];

  /** Active step (1-based) used to drive the progress indicator. */
  get activeStep(): number {
    return this.isPreviewed ? 3 : this.currentStep;
  }

  get isProcessing(): boolean {
    return this.uploadState.processing;
  }

  get processingPercent(): number {
    return this.uploadState.percent;
  }

  get activeProcessingPhase(): number {
    return this.uploadState.phaseIndex;
  }

  get processingLogs(): string[] {
    return this.uploadState.logs;
  }

  get processingPhases() {
    return this.uploadProgress.phases(this.uploadState.mode);
  }

  get elapsedDisplay(): string {
    return this.uploadProgress.elapsedDisplay(this.uploadState.elapsedSeconds);
  }

  get estimatedMinutesRemaining(): number {
    return Math.max(1, Math.ceil(((100 - this.processingPercent) / 100) * 3));
  }

  get currentPhaseLabel(): string {
    return (
      this.processingPhases[this.activeProcessingPhase]?.label || 'Processing'
    );
  }

  /** Maps a category name to a representative bootstrap icon. */
  getCategoryIcon(label: string = ''): string {
    const name = label.toLowerCase();
    if (name.includes('furniture')) return 'bi-truck';
    if (name.includes('vehicle') || name.includes('car')) return 'bi-car-front';
    if (name.includes('pallet')) return 'bi-box-seam';
    if (name.includes('package') || name.includes('box')) return 'bi-boxes';
    return 'bi-grid';
  }

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private activeModal = inject(NgbActiveModal);
  modalService = inject(NgbModal);
  rateManagerService = inject(RateManagerService);
  fb = inject(FormBuilder);
  uploadProgress = inject(BulkUploadProgressService);
  private router = inject(Router);

  constructor() {
    this.uploadState = this.uploadProgress.snapshot;
  }

  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      file: [null, Validators.required],
    });

    // If the service holds a stale completed/failed state (job already done),
    // dismiss it so the modal starts fresh.
    const initialState = this.uploadProgress.snapshot;
    if (
      (initialState.completed || initialState.failed) &&
      !initialState.processing
    ) {
      this.uploadProgress.dismiss();
    }

    // Reflect background progress and restore the result view when reopened.
    this.stateSub = this.uploadProgress.changes.subscribe((state) => {
      this.uploadState = state;
      this.applyUploadState(state);
    });

    this.getCategory();
  }

  ngOnDestroy() {
    this.stateSub?.unsubscribe();
  }

  private applyUploadState(state: BulkUploadState) {
    if (state.completed && state.result && !this.resultApplied) {
      this.resultApplied = true;
      this.isSubmitting = false;

      // If this was a preview validation, navigate to the AG Grid preview page
      if (state.mode === 'preview') {
        this.modalService.dismissAll();
        this.router.navigate(['/instant-rate-manager/bulk-upload-preview']);
        // Reset service state so modal steps are clean on next open
        this.uploadProgress.dismiss();
        return;
      }

      // For actual upload completion, show inline success
      this.previewData = state.result;
      this.isPreviewed = true;
      this.uploadCompleted = state.mode === 'upload';

      const hasErrors = (this.previewData?.errors?.length || 0) > 0;
      const hasWarnings = (this.previewData?.warnings?.length || 0) > 0;
      this.activeTab = hasErrors
        ? 'errors'
        : hasWarnings
          ? 'warnings'
          : 'errors';
    }

    if (state.failed && !this.failApplied) {
      this.failApplied = true;
      this.isSubmitting = false;
      this.isPreviewed = false;
      this.previewData = null;
    }
  }

  closeModal = () => {
    // If a job is still running, keep it alive in the background pill.
    if (this.uploadState.processing) {
      this.uploadProgress.minimize();
    }
    this.modalService.dismissAll();
  };

  async getCategory() {
    try {
      const response =
        await this.rateManagerService.getProviderRatesCategories();
      const mapped = response.data.map((item: any) => ({
        value: item.id,
        label: item.name
          ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
          : item.name,
        description: item.description,
      }));

      this.categoryTypeArray = mapped;

      return mapped;
    } catch (error) {
      console.error('Error fetching category types:', error);
      this.categoryTypeArray = [];
    }
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;

    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.label,
    });
  };

  goToPrevStep = () => {
    if (this.isPreviewed) {
      this.isPreviewed = false;
      this.previewData = null;
      this.activeTab = 'errors';
      this.uploadedFile = null;
      this.uploadCompleted = false;
      this.fileError = null;
      this.rateForm.patchValue({ file: null });
      this.resetFileInput();
      return;
    }

    if (this.currentStep > 1) {
      this.currentStep--;

      if (this.currentStep === 1) {
        this.uploadedFile = null;
        this.rateForm.patchValue({ file: null });
        this.resetFileInput();
      }
    }

    // Disable save button on step 1
    this.isSaveButtonEnabled = this.currentStep > 1;
  };

  private resetFileInput() {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  clearFile() {
    this.uploadedFile = null;
    this.fileError = null;
    this.resetFileInput();
  }

  closeAndReset() {
    this.resetUploadState();
    this.activeModal.close('success');
  }

  goToNextStep = async () => {
    this.currentStep++;

    switch (this.currentStep) {
      case 2:
        if (!this.selectedCategoryID) return;

        this.isSaveButtonEnabled = true;
        break;

      default:
        break;
    }
  };

  handleBulkUpload() {
    if (!this.uploadedFile) {
      this.fileError = 'Please select a CSV file before submitting.';
      return;
    }

    this.fileError = null;
    this.rateForm.patchValue({ file: this.uploadedFile });

    // Move to stage 3 immediately so the progress UI shows inline
    this.isPreviewed = true;

    this.submitBulkRate();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    this.handleFile(file);
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      this.fileError = 'Only CSV files are allowed.';
      return;
    }

    if (file.size === 0) {
      this.fileError = 'File is empty — please select a valid CSV.';
      return;
    }

    this.fileError = null;
    this.uploadedFile = file;
  }

  async submitBulkRate() {
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      await AlertUtil.error('Please fill all required fields.');
      return;
    }

    if (!this.uploadedFile) {
      await AlertUtil.error('Please select a CSV file before submitting.');
      return;
    }

    const formData = new FormData();
    formData.append(
      'rate_category_id',
      this.rateForm.get('rate_category_id')?.value,
    );
    formData.append(
      'rate_category_name',
      this.rateForm.get('rate_category_name')?.value,
    );
    formData.append('file', this.uploadedFile);

    this.resultApplied = false;
    this.failApplied = false;
    this.isSubmitting = true;

    // Use the new async job-based flow:
    // 1. POST /upload-rate → get job_id
    // 2. Poll GET /upload-jobs/{jobId} until validated/failed
    // 3. On validated → navigate to preview page (handled in applyUploadState)
    this.uploadProgress
      .startWithPolling({
        mode: 'preview',
        fileName: this.uploadedFile.name,
        categoryName: this.selectedCategory,
        categoryId: this.selectedCategoryID,
        uploadRequest: () => this.rateManagerService.uploadRateCsv(formData),
      })
      .catch(() => {
        /* surfaced via state subscription + service toast */
      });
  }

  /** Minimize to the floating pill and let processing continue in the background. */
  minimizeProcessing() {
    this.uploadProgress.minimize();
    this.showProcessingLog = false;
    this.modalService.dismissAll();
  }

  toggleProcessingLog() {
    this.showProcessingLog = !this.showProcessingLog;
  }

  private resetUploadState() {
    this.rateForm.reset();
    this.uploadedFile = null;
    this.isPreviewed = false;
    this.previewData = null;
    this.activeTab = 'errors';
    this.currentStep = 1;
    this.selectedCategoryID = undefined!;
    this.selectedCategory = '';
    this.isSaveButtonEnabled = false;
    this.uploadCompleted = false;
    this.fileError = null;
    this.resetFileInput();
  }

  setActiveTab(tab: 'errors' | 'warnings') {
    this.activeTab = tab;
  }

  downloadTemplate = () => {
    const selectedCategoryId = this.selectedCategoryID;
    const selectedCategory = this.selectedCategory;

    this.rateManagerService.downloadRateTemplate(selectedCategoryId).subscribe({
      next: (csvText: string) => {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:T]/g, '');
        link.download = `template_${selectedCategory}.csv`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      },
      error: (err: any) => {
        console.error('Failed to download CSV:', err);
      },
    });
  };
}
